import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import * as Sentry from '@sentry/react';
import { 
  CreditCard, 
  Zap, 
  Gift, 
  TrendingUp, 
  Calendar,
  ArrowRight,
  Check,
  Star,
  Users,
  Target,
  DollarSign,
  Activity,
  RefreshCw,
  ExternalLink,
  Copy,
  AlertCircle,
  CheckCircle,
  Package,
  BarChart3,
  Clock,
  Award,
  ArrowUp,
  ArrowDown,
  X,
  CreditCard as CreditCardIcon,
  Trash2,
  Shield,
  HardDrive,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CreditService } from '../lib/credits';
import { CreditAnalyticsService } from '../lib/credit-analytics';
import { CreditPurchaseService } from '../lib/credit-purchase';
import { ReferralSystemService } from '../lib/referral-system';
import { CustomerPortalService } from '../lib/customer-portal-service';
import { retrieveCheckoutSession } from '../lib/stripe-checkout';
import { UnifiedCreditService } from '../lib/unified-credit-service';
import { AdCreativeCreditService } from '../lib/ad-creative-credits';
import UsageMeterWidget from '../components/widgets/UsageMeterWidget';
import SubscriptionPlansModal from '../components/subscription/SubscriptionPlansModal';
import { supabase } from '../lib/supabase';

interface TabProps {
  id: string;
  label: string;
  icon: React.ElementType;
}

const tabs: TabProps[] = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'purchase', label: 'Purchase Credits', icon: Package },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'referrals', label: 'Referrals', icon: Gift }
];

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  popular?: boolean;
  originalPrice?: number;
}

// Credit packages will be loaded from database via CreditPurchaseService

export function CreditsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptionContext, setSubscriptionContext] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [referrals, setReferrals] = useState<any>(null);
  const [userReferralCode, setUserReferralCode] = useState<string | null>(null);
  const [referralStats, setReferralStats] = useState<any>(null);
  const [creditData, setCreditData] = useState<any>(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<string | null>(null);
  const [creditPackages, setCreditPackages] = useState<any[]>([]);
  const [adCreativePackages, setAdCreativePackages] = useState<any[]>([]);
  const [copyNotification, setCopyNotification] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [organizationOwner, setOrganizationOwner] = useState<{id: string, email: string} | null>(null);

  useEffect(() => {
    loadData();
    
    // Handle navigation state (e.g., from insufficient credits error)
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
    
    // Handle checkout/portal returns
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('portal') === 'success') {
      handlePortalReturn();
    } else if (urlParams.get('checkout') === 'success') {
      handleCheckoutSuccess(urlParams.get('session_id'));
    } else if (urlParams.get('checkout') === 'cancelled') {
      handleCheckoutCancel();
    } else if (urlParams.get('purchase_success') === 'true') {
      handleCreditPurchaseSuccess(urlParams.get('session_id'));
    } else if (urlParams.get('purchase_cancelled') === 'true') {
      handleCreditPurchaseCancel();
    }
  }, [user]);

  const loadOrGenerateReferralCode = async (): Promise<string | null> => {
    try {
      const result = await ReferralSystemService.generateReferralCode();
      return result.success ? result.referralCode || null : null;
    } catch (error) {
      console.error('Error loading/generating referral code:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'loadReferralCode',
          component: 'CreditsPage',
        },
      });
      return null;
    }
  };

  const loadReferralStats = async () => {
    try {
      return await ReferralSystemService.getReferralStats();
    } catch (error) {
      console.error('Error loading referral stats:', error);
      return null;
    }
  };

  const loadAnalyticsData = async () => {
    try {
      if (!user?.id) return null;
      
      // Calculate analytics locally (RPC function not needed)
      return await calculateAnalyticsLocally(user.id);
    } catch (error) {
      console.error('Error loading analytics data:', error);
      return null;
    }
  };

  const loadOrganizationOwner = async () => {
    try {
      // For personal accounts (no organization), they are their own "owner"
      if (!user?.organization_id) {
        return {
          id: user.id,
          email: user.email,
          isPersonalAccount: true
        };
      }

      // Get organization details and owner information
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('created_by')
        .eq('id', user.organization_id)
        .single();

      if (orgError || !organization?.created_by) {
        console.error('Error loading organization:', orgError);
        return null;
      }

      // Get owner's email
      const { data: ownerData, error: ownerError } = await supabase
        .from('users')
        .select('email')
        .eq('id', organization.created_by)
        .single();

      if (ownerError || !ownerData) {
        console.error('Error loading owner data:', ownerError);
        return null;
      }

      return {
        id: organization.created_by,
        email: ownerData.email,
        isPersonalAccount: false
      };
    } catch (error) {
      console.error('Error loading organization owner:', error);
      return null;
    }
  };

  const calculateAnalyticsLocally = async (userId: string) => {
    try {
      // Get current user data to check for organization (match Dashboard logic)
      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userId)
        .single();

      let userIds = [userId];
      
      // If user belongs to an organization, include all organization members' analyses
      if (currentUser?.organization_id) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', currentUser.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }

      // Get analyses directly by user_id (includes all analyses regardless of creative linkage)
      const { data: analyses, error: analysesError } = await supabase
        .from('analyses')
        .select('id, overall_score, created_at, platforms')
        .in('user_id', userIds)
        .not('overall_score', 'is', null)
        .order('created_at', { ascending: false });

      if (analysesError || !analyses) {
        console.log('❌ Error getting analyses:', analysesError);
        return null;
      }

      if (analyses.length === 0) return null;

      // Calculate statistics - match Dashboard's launch ready logic (80+ threshold)
      const totalAnalyses = analyses.length;
      const launchReadyAnalyses = analyses.filter(a => (a.overall_score ?? 0) >= 80).length;
      const successRate = totalAnalyses > 0 ? (launchReadyAnalyses / totalAnalyses) * 100 : 0;
      
      // Current month statistics - fix timezone issues
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      
      const currentMonthAnalyses = analyses.filter(a => {
        const analysisDate = new Date(a.created_at || new Date());
        return analysisDate >= startOfMonth;
      });
      
      const currentMonthUsed = currentMonthAnalyses.length;
      const currentMonthLaunchReady = currentMonthAnalyses.filter(a => (a.overall_score ?? 0) >= 80).length;
      const currentMonthSuccessRate = currentMonthUsed > 0 ? (currentMonthLaunchReady / currentMonthUsed) * 100 : 0;

      // Daily usage for last 30 days
      const last30Days = new Date();
      last30Days.setDate(last30Days.getDate() - 30);
      
      const recentAnalyses = analyses.filter(a => 
        new Date(a.created_at || new Date()) >= last30Days
      );

      // Group by day
      const dailyUsage = new Map();
      recentAnalyses.forEach(analysis => {
        const date = new Date(analysis.created_at || new Date()).toDateString();
        if (!dailyUsage.has(date)) {
          dailyUsage.set(date, { count: 0, launchReady: 0, totalScore: 0 });
        }
        const dayData = dailyUsage.get(date);
        dayData.count++;
        if ((analysis.overall_score ?? 0) >= 80) dayData.launchReady++;
        dayData.totalScore += analysis.overall_score || 0;
      });

      const result = {
        totalAnalyses,
        launchReadyAnalyses,
        successRate,
        averageScore: totalAnalyses > 0 ? analyses.reduce((sum, a) => sum + (a.overall_score || 0), 0) / totalAnalyses : 0,
        currentMonth: {
          used: currentMonthUsed,
          launchReady: currentMonthLaunchReady,
          successRate: currentMonthSuccessRate
        },
        last30Days: {
          analyses: recentAnalyses.length,
          launchReady: recentAnalyses.filter(a => (a.overall_score ?? 0) >= 80).length
        },
        dailyUsage: Array.from(dailyUsage.entries()).map(([date, data]) => ({
          date,
          count: data.count,
          launchReady: data.launchReady,
          avgScore: data.count > 0 ? data.totalScore / data.count : 0
        })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      };

      return result;
    } catch (error) {
      console.error('Error calculating analytics locally:', error);
      return null;
    }
  };

  const loadData = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const [context, oldAnalyticsData, referralData, credits, packages, adPackages, referralCode, stats, newAnalyticsData, unifiedCredits, owner] = await Promise.all([
        CustomerPortalService.getSubscriptionContext(user.id),
        Promise.resolve(null), // CreditAnalyticsService.getAnalytics(user.id) - method doesn't exist yet
        Promise.resolve(null), // ReferralSystemService.getReferralStats(user.id) - method doesn't exist yet
        CreditService.getDetailedCreditInfo(),
        CreditPurchaseService.getCreditPackages(),
        AdCreativeCreditService.getAvailablePackages(),
        loadOrGenerateReferralCode(),
        loadReferralStats(),
        loadAnalyticsData(),
        UnifiedCreditService.getCreditData(user.id),
        loadOrganizationOwner()
      ]);
      setSubscriptionContext(context);
      setAnalytics(oldAnalyticsData);
      setReferrals(referralData);
      setCreditData(unifiedCredits); // Use unified credit data instead
      setCreditPackages(packages);
      setAdCreativePackages(adPackages);
      setUserReferralCode(referralCode);
      setReferralStats(stats);
      setAnalyticsData(newAnalyticsData);
      setOrganizationOwner(owner);
    } catch (error) {
      console.error('Error loading data:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'loadData',
          component: 'CreditsPage',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePortalReturn = async () => {
    if (!user?.id) return;
    
    try {
      const result = await CustomerPortalService.handlePortalReturn(user.id);
      console.log('Portal return result:', result);
      
      // Refresh data after portal return
      await loadData();
    } catch (error) {
      console.error('Error handling portal return:', error);
    }
  };

  const handleCheckoutSuccess = async (sessionId: string | null) => {
    if (!user?.id || !sessionId) return;
    
    try {
      setCheckoutStatus('processing');
      const session = await retrieveCheckoutSession(sessionId);
      
      if (session.payment_status === 'paid') {
        setCheckoutStatus('success');
        // Refresh data to show new subscription
        await loadData();
        // Clear URL parameters
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        setCheckoutStatus('error');
        console.error('Payment not completed:', session.payment_status);
      }
    } catch (error) {
      console.error('Error handling checkout success:', error);
      setCheckoutStatus('error');
    }
  };

  const handleCheckoutCancel = () => {
    setCheckoutStatus('cancelled');
    // Clear URL parameters
    window.history.replaceState({}, '', window.location.pathname);
  };

  const handleCreditPurchaseSuccess = async (sessionId: string | null) => {
    if (!user?.id || !sessionId) return;
    
    try {
      setCheckoutStatus('processing');
      
      // Try to determine if this is an ad creative purchase by checking session metadata
      const { data: sessionVerification } = await supabase.functions.invoke('stripe-checkout-v2', {
        body: {
          action: 'verify_session_status',
          params: { session_id: sessionId }
        }
      });
      
      const isAdCreativePurchase = sessionVerification?.metadata?.credit_type === 'ad_creative';
      
      let result;
      if (isAdCreativePurchase) {
        // Use ad creative credit verification with automatic processing
        result = await AdCreativeCreditService.verifyAndProcessPurchase(sessionId);
      } else {
        // Use analysis credit verification
        result = await CreditPurchaseService.verifyCheckoutSession(sessionId);
      }
      
      if (result.success && result.paymentCompleted) {
        setCheckoutStatus('success');
        // Refresh data to show new credit balance
        await loadData();
        // Clear URL parameters
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        setCheckoutStatus('error');
        console.error('Credit purchase not completed:', result);
      }
    } catch (error) {
      console.error('Error verifying credit purchase:', error);
      setCheckoutStatus('error');
    }
  };

  const handleCreditPurchaseCancel = () => {
    setCheckoutStatus('cancelled');
    // Clear URL parameters
    window.history.replaceState({}, '', window.location.pathname);
  };

  const handleViewPlans = () => {
    setShowPlansModal(true);
  };

  const handleSubscriptionSuccess = async (planId: string) => {
    setShowPlansModal(false);
    setCheckoutStatus('success');
    // Refresh data to show new subscription
    await loadData();
  };

  const handleManageBilling = async () => {
    if (!user?.id) return;
    
    try {
      setRedirecting(true);
      await CustomerPortalService.redirectToPortal(
        user.id, 
        `${window.location.origin}/dashboard/credits?portal=success`
      );
    } catch (error) {
      console.error('Error opening customer portal:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'openCustomerPortal',
          component: 'CreditsPage',
        },
      });
      alert('Unable to open billing portal. Please try again or contact support.');
    } finally {
      setRedirecting(false);
    }
  };

  const handlePurchaseCredits = async (packageId: string) => {
    try {
      const result = await CreditPurchaseService.purchaseCreditPackage({
        packageId,
        successUrl: `${window.location.origin}/dashboard/credits?purchase_success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/dashboard/credits?purchase_cancelled=true`
      });

      if (result.success && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        console.error('Purchase failed:', result.error);
        alert(result.error || 'Failed to initiate purchase. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'createCheckoutSession',
          component: 'CreditsPage',
        },
      });
      alert('Failed to initiate purchase. Please try again.');
    }
  };

  const handlePurchaseAdCreativeCredits = async (packageId: string) => {
    try {
      const result = await AdCreativeCreditService.purchasePackage(packageId);

      if (result.success && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        console.error('Purchase failed:', result.error);
        alert(result.error || 'Failed to initiate purchase. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'createCheckoutSession',
          component: 'CreditsPage',
        },
      });
      alert('Failed to initiate purchase. Please try again.');
    }
  };

  // Helper function to show copy notifications
  const showCopyNotification = (message: string) => {
    setCopyNotification(message);
    setTimeout(() => setCopyNotification(null), 3000);
  };

  // Helper function to check if current user is organization owner or personal account
  const isOrganizationOwner = () => {
    // Personal accounts (no organization) are always "owners" of their own account
    if (!user?.organization_id) {
      return true;
    }
    
    // For organization accounts, check if user is the organization owner
    return user?.id && organizationOwner?.id && user.id === organizationOwner.id;
  };

  // Helper function to get social sharing URLs
  const getSocialShareUrls = (referralCode: string) => {
    const shareUrl = `${window.location.origin}/onboarding?ref=${referralCode}`;
    const message = "🚀 Join me on Rulevia and get AI-powered creative analysis for your ads! Perfect for marketers who want to ensure compliance and boost performance across all platforms.";
    const hashtags = "marketing,advertising,AI,compliance,digitalmarketing";
    
    return {
      linkedin: `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(message + " " + shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`,
      facebook: `https://www.facebook.com/share.php?u=${encodeURIComponent(shareUrl)}`,
      reddit: `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(message)}`
    };
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <SEOHead {...getPageSEO('/credits')} />
      {/* Copy Notification */}
      {copyNotification && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-auto z-50 bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
        >
          <Check className="w-5 h-5" />
          <span>{copyNotification}</span>
        </motion.div>
      )}

      {/* Page Header */}
      <div className="mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Credits & Subscription</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Manage your subscription, purchase additional credits, and track your usage analytics.
          </p>
        </motion.div>
      </div>

      {/* Checkout Status Messages */}
      {checkoutStatus && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg border ${
            checkoutStatus === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800'
              : checkoutStatus === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : checkoutStatus === 'cancelled'
              ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}
        >
          <div className="flex items-center">
            {checkoutStatus === 'success' && <CheckCircle className="w-5 h-5 mr-2" />}
            {checkoutStatus === 'error' && <AlertCircle className="w-5 h-5 mr-2" />}
            {checkoutStatus === 'cancelled' && <X className="w-5 h-5 mr-2" />}
            {checkoutStatus === 'processing' && <RefreshCw className="w-5 h-5 mr-2 animate-spin" />}
            
            <span className="font-medium">
              {checkoutStatus === 'success' && 'Purchase completed successfully! Your credits have been added to your account.'}
              {checkoutStatus === 'error' && 'There was an issue with your purchase. Please try again or contact support.'}
              {checkoutStatus === 'cancelled' && 'Purchase was cancelled. You can try again anytime.'}
              {checkoutStatus === 'processing' && 'Processing your purchase...'}
            </span>
            
            <button
              onClick={() => setCheckoutStatus(null)}
              className="ml-auto p-1 hover:bg-black hover:bg-opacity-10 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}


      {/* Tab Navigation */}
      <div className="mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="border-b border-gray-200"
        >
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-h-[44px] ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </nav>
        </motion.div>
      </div>

      {/* Tab Content */}
      <div className="space-y-8">
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8"
          >
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              {/* Current Subscription - Modified Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Current Subscription</h2>
                
                
                {subscriptionContext?.hasAccess ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {subscriptionContext.planName || 'Active Plan'}
                            </h3>
                            <p className="text-sm text-gray-600 capitalize">
                              {subscriptionContext.status}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {subscriptionContext.nextBillingDate && (
                            <p className="text-sm text-gray-600">
                              Next billing: {new Date(subscriptionContext.nextBillingDate).toLocaleDateString()}
                            </p>
                          )}
                          {subscriptionContext.cancelAtPeriodEnd && (
                            <p className="text-sm text-orange-600 font-medium">
                              <AlertCircle className="w-4 h-4 inline mr-1" />
                              Cancels at period end
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {isOrganizationOwner() ? (
                      <>
                        <button
                          onClick={handleManageBilling}
                          disabled={redirecting}
                          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                        >
                          {redirecting ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Opening Portal...</span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4" />
                              <span>Manage Subscription</span>
                              <ExternalLink className="w-4 h-4" />
                            </>
                          )}
                        </button>
                        
                        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                          <p className="font-medium mb-1">What you can do:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Upgrade or downgrade your plan</li>
                            <li>• Cancel your subscription</li>
                            <li>• Update payment methods</li>
                            <li>• View billing history</li>
                            <li>• Download invoices</li>
                          </ul>
                        </div>
                      </>
                    ) : user?.organization_id ? (
                      <>
                        <div className="w-full bg-gray-100 text-gray-500 px-4 sm:px-6 py-3 rounded-lg flex items-center justify-center space-x-2 min-h-[44px] cursor-not-allowed">
                          <AlertCircle className="w-4 h-4" />
                          <span>Manage Subscription</span>
                        </div>
                        
                        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium mb-1">Access Restricted</p>
                            <p>
                              Only the organization owner can manage the subscription. 
                              {organizationOwner?.email && (
                                <>
                                  <br />
                                  Contact <strong>{organizationOwner.email}</strong> for subscription changes.
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={handleManageBilling}
                          disabled={redirecting}
                          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                        >
                          {redirecting ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Opening Portal...</span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4" />
                              <span>Manage Subscription</span>
                              <ExternalLink className="w-4 h-4" />
                            </>
                          )}
                        </button>
                        
                        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                          <p className="font-medium mb-1">What you can do:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Upgrade or downgrade your plan</li>
                            <li>• Cancel your subscription</li>
                            <li>• Update payment methods</li>
                            <li>• View billing history</li>
                            <li>• Download invoices</li>
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="p-3 bg-gray-100 rounded-full w-fit mx-auto mb-4">
                      <AlertCircle className="w-8 h-8 text-gray-500" />
                    </div>
                    <h3 className="font-medium text-gray-900 mb-2">No Active Subscription</h3>
                    <p className="text-gray-600 mb-4">
                      Subscribe to access premium features and higher usage limits.
                    </p>
                    <button 
                      onClick={handleViewPlans}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all min-h-[44px]"
                    >
                      View Plans
                    </button>
                  </div>
                )}
              </div>

              {/* Storage Usage - now displays real data */}

              {/* Billing Security Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-blue-900 mb-2">Secure Billing Portal</h3>
                    <p className="text-blue-800 text-sm">
                      All billing operations are handled securely through Stripe's customer portal. 
                      Your payment information is never stored on our servers and is protected by 
                      industry-standard encryption.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Usage Overview */}
            <div className="space-y-4 sm:space-y-6">
              <UsageMeterWidget showDebugInfo={false} />
            </div>
          </motion.div>
        )}

        {activeTab === 'purchase' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6 sm:space-y-8"
          >
            {/* Owner Restriction Notice for Non-Owners (Only for organization members, not personal accounts) */}
            {!isOrganizationOwner() && user?.organization_id && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-900 mb-2">Purchase Restrictions</h3>
                    <p className="text-amber-800 text-sm mb-2">
                      Only the organization owner can purchase additional credits for the team.
                    </p>
                    {organizationOwner?.email && (
                      <p className="text-amber-700 text-sm">
                        Contact <strong>{organizationOwner.email}</strong> to purchase credits for your organization.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analysis Credit Packages */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <CreditCard className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Analysis Credit Packs</h2>
              </div>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                Purchase additional analysis credits for compliance and performance checks. Credits never expire.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {creditPackages.map((pkg, index) => {
                  const price = pkg.price_cents / 100; // Convert cents to dollars
                  const pricePerCredit = pkg.price_per_credit ?? price / pkg.credits;
                  const isPopular = pkg.name === 'Standard Pack'; // Mark Standard Pack as popular
                  
                  return (
                    <motion.div
                      key={pkg.id}
                      whileHover={{ scale: 1.02 }}
                      className={`relative bg-white rounded-xl border-2 p-4 sm:p-6 transition-all ${
                        isPopular 
                          ? 'border-purple-300 shadow-lg' 
                          : 'border-gray-200 hover:border-purple-200'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Most Popular
                          </span>
                        </div>
                      )}
                      
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{pkg.name}</h3>
                        <div className="text-3xl font-bold text-purple-600 mb-1">
                          {pkg.credits}
                        </div>
                        <div className="text-sm text-gray-600 mb-4">credits</div>
                        
                        <div className="text-2xl font-bold text-gray-900 mb-4">
                          {formatCurrency(price)}
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-6">
                          {formatCurrency(pricePerCredit)} per credit
                        </div>
                        
                        {pkg.discount_percentage > 0 && (
                          <div className="text-sm text-green-600 mb-4 font-medium">
                            {pkg.discount_percentage}% off!
                          </div>
                        )}
                        
                        {isOrganizationOwner() ? (
                          <button
                            onClick={() => handlePurchaseCredits(pkg.id)}
                            className={`w-full py-3 px-4 rounded-lg font-medium transition-all min-h-[44px] ${
                              isPopular
                                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Purchase Credits
                          </button>
                        ) : (
                          <button
                            disabled
                            className="w-full py-3 px-4 rounded-lg font-medium transition-all min-h-[44px] bg-gray-100 text-gray-400 cursor-not-allowed"
                            title={`Only the organization owner (${organizationOwner?.email || 'owner'}) can purchase credits`}
                          >
                            Owner Only
                          </button>
                        )}
                        
                        {pkg.description && (
                          <div className="text-xs text-gray-500 mt-3">
                            {pkg.description}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Ad Creative Credit Packages */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Creative Credit Packs</h2>
              </div>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                Purchase credits for AI-powered ad generation, enhancement, and regeneration. Credits never expire.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {adCreativePackages.map((pkg) => {
                  const price = pkg.priceCents / 100; // Convert cents to dollars
                  const pricePerCredit = price / pkg.credits;
                  const isPopular = pkg.name === 'Standard Pack'; // Mark Standard Pack as popular
                  
                  return (
                    <motion.div
                      key={pkg.id}
                      whileHover={{ scale: 1.02 }}
                      className={`relative bg-white rounded-xl border-2 p-4 sm:p-6 transition-all ${
                        isPopular 
                          ? 'border-amber-300 shadow-lg' 
                          : 'border-gray-200 hover:border-amber-200'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Best Value
                          </span>
                        </div>
                      )}
                      
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{pkg.name}</h3>
                        <div className="text-3xl font-bold text-amber-600 mb-1">
                          {pkg.credits}
                        </div>
                        <div className="text-sm text-gray-600 mb-4">creative credits</div>
                        
                        <div className="text-2xl font-bold text-gray-900 mb-4">
                          {formatCurrency(price)}
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-6">
                          {formatCurrency(pricePerCredit)} per credit
                        </div>
                        
                        {pkg.discountPercentage > 0 && (
                          <div className="text-sm text-green-600 mb-4 font-medium">
                            {pkg.discountPercentage}% off!
                          </div>
                        )}
                        
                        {isOrganizationOwner() ? (
                          <button
                            onClick={() => handlePurchaseAdCreativeCredits(pkg.id)}
                            className={`w-full py-3 px-4 rounded-lg font-medium transition-all min-h-[44px] ${
                              isPopular
                                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Purchase Credits
                          </button>
                        ) : (
                          <button
                            disabled
                            className="w-full py-3 px-4 rounded-lg font-medium transition-all min-h-[44px] bg-gray-100 text-gray-400 cursor-not-allowed"
                            title={`Only the organization owner (${organizationOwner?.email || 'owner'}) can purchase credits`}
                          >
                            Owner Only
                          </button>
                        )}
                        
                        {pkg.description && (
                          <div className="text-xs text-gray-500 mt-3">
                            {pkg.description}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6 sm:space-y-8"
          >
            {/* Credit Usage Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-600">Analyses This Month</h3>
                  <Activity className="w-4 h-4 text-blue-500" />
                </div>
                <div className="text-lg sm:text-2xl font-bold text-gray-900">
                  {creditData?.currentMonthUsed || analyticsData?.currentMonth?.used || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1 hidden sm:block">
                  {creditData?.currentMonthUsed || analyticsData?.currentMonth?.used ? `${creditData?.currentMonthUsed || analyticsData?.currentMonth?.used} total analyses performed` : 'No analyses this month'}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-600">Success Rate</h3>
                  <Target className="w-4 h-4 text-green-500" />
                </div>
                <div className="text-lg sm:text-2xl font-bold text-gray-900">
                  {creditData?.successRate ? `${Math.round(creditData.successRate)}%` : analyticsData?.successRate ? `${Math.round(analyticsData.successRate)}%` : '0%'}
                </div>
                <div className="text-xs text-green-600 mt-1 hidden sm:block">
                  {creditData?.launchReadyCount || analyticsData?.launchReadyAnalyses || 0} launch-ready creatives
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-600">Total Analyses</h3>
                  <BarChart3 className="w-4 h-4 text-orange-500" />
                </div>
                <div className="text-lg sm:text-2xl font-bold text-gray-900">
                  {creditData?.totalAnalyses || analyticsData?.totalAnalyses || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1 hidden sm:block">
                  Lifetime total
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-600">Average Score</h3>
                  <Star className="w-4 h-4 text-purple-500" />
                </div>
                <div className="text-lg sm:text-2xl font-bold text-gray-900">
                  {analyticsData?.averageScore ? Math.round(analyticsData.averageScore) : 0}
                </div>
                <div className="text-xs text-purple-600 mt-1 hidden sm:block">
                  Overall performance
                </div>
              </div>
            </div>

            {/* Usage Activity Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                Recent Activity Summary
                {user?.organization_id && (
                  <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    Organization-wide
                  </span>
                )}
              </h3>
              
              {analyticsData?.dailyUsage && analyticsData.dailyUsage.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{analyticsData.last30Days?.analyses || 0}</div>
                      <div className="text-sm text-blue-700">Analyses (30 days)</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{analyticsData.last30Days?.launchReady || 0}</div>
                      <div className="text-sm text-green-700">Launch-ready</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {analyticsData.last30Days?.analyses > 0 ? Math.round((analyticsData.last30Days.launchReady / analyticsData.last30Days.analyses) * 100) : 0}%
                      </div>
                      <div className="text-sm text-purple-700">Success rate</div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h4>
                    <div className="space-y-2">
                      {analyticsData.dailyUsage.slice(0, 3).map((day: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="text-sm font-medium text-gray-900">
                              {new Date(day.date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-gray-500">
                              {day.count} analyses
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="text-sm text-green-600">
                              {day.launchReady} launch-ready
                            </div>
                            <div className="text-xs text-gray-500">
                              ({day.count > 0 ? Math.round((day.launchReady / day.count) * 100) : 0}%)
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No recent activity</p>
                  <p className="text-xs text-gray-400 mt-1">Start analyzing creatives to see your activity</p>
                </div>
              )}
            </div>

            {/* Quick Insights */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-500" />
                Performance Insights
                {user?.organization_id && (
                  <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    Organization-wide
                  </span>
                )}
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 text-sm">📊</span>
                  </div>
                  <p className="text-gray-700">
                    {user?.organization_id ? 'Your organization has' : 'You\'ve'} completed <strong>{analyticsData?.totalAnalyses || 0} total analyses</strong> with an average score of <strong>{analyticsData?.averageScore ? Math.round(analyticsData.averageScore) : 0}</strong>
                  </p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm">✅</span>
                  </div>
                  <p className="text-gray-700">
                    <strong>{analyticsData?.launchReadyAnalyses || 0} creatives</strong> ({analyticsData?.successRate ? `${Math.round(analyticsData.successRate)}%` : '0%'}) are launch-ready (score ≥ 80)
                  </p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-600 text-sm">📅</span>
                  </div>
                  <p className="text-gray-700">
                    This month: <strong>{analyticsData?.currentMonth?.used || 0} analyses</strong> with <strong>{analyticsData?.currentMonth?.launchReady || 0} launch-ready</strong> ({analyticsData?.currentMonth?.successRate ? Math.round(analyticsData.currentMonth.successRate) : 0}% success rate)
                  </p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-orange-600 text-sm">🎯</span>
                  </div>
                  <p className="text-gray-700">
                    {analyticsData?.totalAnalyses > 0 
                      ? `Top performing platform: Analysis data not yet available`
                      : `No platform analysis data available yet`
                    }
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'referrals' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6 sm:space-y-8"
          >
            {/* Referral Overview */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-4 sm:p-8 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-2">Invite Friends & Earn Credits</h2>
                  <p className="text-sm sm:text-base text-purple-100">
                    Earn 10 credits for every friend who signs up for a paid subscription. They get 10 credits too!
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold mb-1">
                    {referralStats?.totalCreditsEarned || 0}
                  </div>
                  <div className="text-purple-100 text-sm">Credits Earned</div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {referralStats?.totalReferrals || 0}
                  </div>
                  <div className="text-purple-100 text-sm">Total Invites</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {referralStats?.successfulSignups || 0}
                  </div>
                  <div className="text-purple-100 text-sm">Successful Signups</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {referralStats?.conversionRate ? `${referralStats.conversionRate.toFixed(1)}%` : '0%'}
                  </div>
                  <div className="text-purple-100 text-sm">Conversion Rate</div>
                </div>
              </div>
            </div>

            {/* Your Referral Code */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <Gift className="w-5 h-5 mr-2 text-purple-600" />
                Your Referral Code
              </h3>
              
              {userReferralCode ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Your unique referral code:</div>
                        <div className="text-2xl font-mono font-bold text-purple-600">
                          {userReferralCode}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(userReferralCode);
                          showCopyNotification('Referral code copied to clipboard!');
                        }}
                        className="flex items-center space-x-2 bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors min-h-[44px]"
                      >
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Copy Link Button */}
                    <button
                      onClick={() => {
                        const shareUrl = `${window.location.origin}/onboarding?ref=${userReferralCode}`;
                        navigator.clipboard.writeText(shareUrl);
                        showCopyNotification('Signup link copied to clipboard!');
                      }}
                      className="w-full flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Copy Signup Link</span>
                    </button>
                    
                    {/* Social Sharing Section */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 text-center">Share on Social Media</h4>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        {/* LinkedIn */}
                        <button
                          onClick={() => {
                            const urls = getSocialShareUrls(userReferralCode);
                            window.open(urls.linkedin, '_blank', 'width=600,height=400');
                          }}
                          className="flex items-center justify-center space-x-1 sm:space-x-2 bg-blue-600 text-white px-2 sm:px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors min-h-[44px] text-sm"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                          <span>LinkedIn</span>
                        </button>
                        
                        {/* X (Twitter) */}
                        <button
                          onClick={() => {
                            const urls = getSocialShareUrls(userReferralCode);
                            window.open(urls.twitter, '_blank', 'width=600,height=400');
                          }}
                          className="flex items-center justify-center space-x-1 sm:space-x-2 bg-gray-900 text-white px-2 sm:px-4 py-3 rounded-lg hover:bg-gray-800 transition-colors min-h-[44px] text-sm"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                          <span>X (Twitter)</span>
                        </button>
                        
                        {/* Facebook */}
                        <button
                          onClick={() => {
                            const shareUrl = `${window.location.origin}/onboarding?ref=${userReferralCode}`;
                            const message = "🚀 Join me on Rulevia and get AI-powered creative analysis for your ads! Perfect for marketers who want to ensure compliance and boost performance across all platforms.";
                            
                            // Copy content to clipboard first
                            navigator.clipboard.writeText(`${message}\n\n${shareUrl}\n\n#marketing #advertising #AI #compliance`);
                            showCopyNotification('Content copied! Paste it in the Facebook share dialog.');
                            
                            // Try to open Facebook share dialog
                            const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                            window.open(facebookShareUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
                          }}
                          className="flex items-center justify-center space-x-1 sm:space-x-2 bg-blue-500 text-white px-2 sm:px-4 py-3 rounded-lg hover:bg-blue-600 transition-colors min-h-[44px] text-sm"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          <span>Facebook</span>
                        </button>
                        
                        {/* Reddit */}
                        <button
                          onClick={() => {
                            const urls = getSocialShareUrls(userReferralCode);
                            window.open(urls.reddit, '_blank', 'width=600,height=400');
                          }}
                          className="flex items-center justify-center space-x-1 sm:space-x-2 bg-orange-500 text-white px-2 sm:px-4 py-3 rounded-lg hover:bg-orange-600 transition-colors min-h-[44px] text-sm"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                          </svg>
                          <span>Reddit</span>
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-500 text-center mt-3">
                        Perfect for reaching marketers and advertising professionals!
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Generating your referral code...</p>
                </div>
              )}
            </div>

            {/* Referral History */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <Users className="w-5 h-5 mr-2 text-purple-600" />
                Referral History
              </h3>
              
              {referralStats?.recentRewards && referralStats.recentRewards.length > 0 ? (
                <div className="space-y-4">
                  {referralStats.recentRewards.map((reward: any, index: number) => (
                    <div key={reward.id || index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          reward.status === 'awarded' ? 'bg-green-500' : 
                          reward.status === 'pending' ? 'bg-yellow-500' : 
                          'bg-gray-400'
                        }`}></div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {reward.reward_type === 'signup_bonus' ? 'Sign-up Bonus' : 
                             reward.reward_type === 'first_purchase' ? 'First Purchase Bonus' : 
                             'Milestone Reward'}
                          </div>
                          <div className="text-sm text-gray-600">
                            {new Date(reward.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-green-600">+{reward.reward_credits} credits</div>
                        <div className={`text-sm capitalize ${
                          reward.status === 'awarded' ? 'text-green-600' : 
                          reward.status === 'pending' ? 'text-yellow-600' : 
                          'text-gray-600'
                        }`}>
                          {reward.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No referrals yet</h4>
                  <p className="text-gray-600 mb-4">
                    Start inviting friends to earn your first referral credits!
                  </p>
                </div>
              )}
            </div>

            {/* How It Works */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-4">How Referrals Work</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h4 className="font-medium text-blue-900 mb-2">Share Your Code</h4>
                  <p className="text-sm text-blue-700">
                    Share your unique referral code with friends and colleagues
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <h4 className="font-medium text-blue-900 mb-2">They Sign Up</h4>
                  <p className="text-sm text-blue-700">
                    Your friend signs up and subscribes to a paid plan (not trial)
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <h4 className="font-medium text-blue-900 mb-2">Both Get Credits</h4>
                  <p className="text-sm text-blue-700">
                    You both receive 10 credits when they complete their first payment
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Subscription Plans Modal */}
      <SubscriptionPlansModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        onSubscriptionSuccess={handleSubscriptionSuccess}
      />
    </div>
  );
}